import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';
import uniqueId from 'lodash/uniqueId';

import WidgetFormService from '../WidgetForm/WidgetFormService';
import widgetFieldTypes from '@/components/BlockEditorWidget/widgetFieldTypes';
import { FieldType, FIELDS_RECEIVING_HTML_AS_INPUT } from '@/components/BlockEditorWidget/widgetFieldTypes.const';

// Private

const getWidgetFieldTitle = (fieldType) => widgetFieldTypes[fieldType].label;

const setObjectValueIfNotExist = (obj, key, val) => (obj[key] = obj[key] || val);

const initializeWidgetFieldStyle = (field) => {
	setObjectValueIfNotExist(field.metadata.style, 'outline', { enabled: true, key: 'outline', value: '#E6E7EC' });
	setObjectValueIfNotExist(field.metadata.style, 'background', { enabled: true, key: 'outline', value: '#E6E7EC' });
	return field;
};

const initializeWidgetFieldOptions = (field) => {
	setObjectValueIfNotExist(field.metadata.options, 'layout', { grid: 'fill' });
	setObjectValueIfNotExist(field.metadata.options, 'identifier', { key: 'identifier', value: '' });
	setObjectValueIfNotExist(field.metadata.options, 'required', { key: 'required', enabled: false });
	setObjectValueIfNotExist(field.metadata.options, 'prefill', { key: 'prefill', value: '', enabled: false });
	setObjectValueIfNotExist(field.metadata.options, 'placeholder', { key: 'placeholder', value: '', enabled: false });
	setObjectValueIfNotExist(field.metadata.options, 'description', { key: 'description', value: '', enabled: false });
	setObjectValueIfNotExist(field.metadata.options, 'previous_steps', { key: 'previous_steps', value: {}, enabled: false });
	setObjectValueIfNotExist(field.metadata.options, 'title', { key: 'title', value: getWidgetFieldTitle(field.field_type), enabled: true });
	return field;
};

const initializeWidgetFieldMetadata = (field) => {
	setObjectValueIfNotExist(field, 'metadata', widgetFieldTypes[field.field_type]?.defaultMetadata || {});
	setObjectValueIfNotExist(field.metadata, 'style', {});
	setObjectValueIfNotExist(field.metadata, 'options', {});

	field = initializeWidgetFieldStyle(field);
	field = initializeWidgetFieldOptions(field);
	return field;
};

const initializeWidgetFieldValuesIfNeeded = (field) => {
	setObjectValueIfNotExist(field, 'id', `temp_${uniqueId()}`);
	field = initializeWidgetFieldMetadata(field);

	return field;
};

// Public

const populateDefaultWidgetValuesIfNeeded = (field) => {
	if (!field) {
		return;
	}

	field = initializeWidgetFieldValuesIfNeeded(field);
	const fieldSpecificDefaultMetaData = cloneDeep(widgetFieldTypes[field.field_type]?.defaultMetadata || {});
	const mergedMetadata = merge(fieldSpecificDefaultMetaData, field.metadata || {});
	field = { ...field, metadata: mergedMetadata };

	return field;
};

/**
 * we used to save multiple selection state of widget field in two diffrenet keys,
 * to prevent confusion we combined them into one source under 'user_selection.multiple' for all widget fields types
 *
 * 'multiple_selection' is deprecated and is here only for backward compatibility
 */
const isMultipleSelection = (field) =>
	field.metadata.options.user_selection?.multiple || field.metadata.options.multiple_selection?.enabled;

const extractInitialValueFromWidgetField = (field) => {
	const { metadata, currentUserResponse } = field;
	if (currentUserResponse) {
		return currentUserResponse;
	}
	if (metadata) {
		const { options, output } = metadata;
		if (output && output.value) {
			return output.value;
		} else if (options) {
			const { prefill } = options;
			if (prefill && prefill.enabled) {
				return prefill.value || '';
			}
		}
	}
	return null;
};

/**
 * This method take care of default value for widget fields, including *prefill* handling
 * see @extractInitialValueFromWidgetField for more details
 * @param {*} field
 * @param {*} previousSteps
 * @param {*} replaceInsertDataStringDict
 * @param {*} configurationsMappingBlocksUUIDs
 * @returns
 */
const getDefaultValueOfField = (field, previousSteps, replaceInsertDataStringDict, configurationsMappingBlocksUUIDs) => {
	const fieldConf = widgetFieldTypes[field.field_type];
	const isComplexValue = isMultipleSelection(field);
	// we clone the defaultValue to prevent changes to the object itself in WidgetFieldTypes
	const defaultValue = cloneDeep(isComplexValue && fieldConf.complexDefaultValue ? fieldConf.complexDefaultValue : fieldConf.defaultValue);
	const isReceivesHtml = FIELDS_RECEIVING_HTML_AS_INPUT.includes(field.field_type);
	let value = extractInitialValueFromWidgetField(field);
	value =
		value && previousSteps
			? WidgetFormService.processPreviousSteps(
					value,
					previousSteps,
					replaceInsertDataStringDict,
					configurationsMappingBlocksUUIDs,
					isReceivesHtml
			  )
			: defaultValue;
	return value;
};

const getFormFields = (allFields) => allFields.filter((field) => !field.hidden && field.field_type !== FieldType.WidgetFooter);
const getWidgetFooterField = (allFields) => allFields.find((field) => field.field_type === FieldType.WidgetFooter);

const WidgetFieldService = {
	populateDefaultWidgetValuesIfNeeded,
	isMultipleSelection,
	getDefaultValueOfField,
	getFormFields,
	getWidgetFooterField,
};

export default WidgetFieldService;
